// Shared sorting / grouping helpers for the Board Portal tabs.
// Keep these pure and side-effect free so they're trivial to test.

export const ROLE_PRIORITY = {
  chair: 0,
  vice_chair: 1,
  treasurer: 2,
  secretary: 3,
  director: 4,
};

const ROLE_PRIORITY_FALLBACK = 99;

export const STATUS_PRIORITY = {
  open: 0,
  in_progress: 1,
  done: 2,
  cancelled: 3,
};

export const RESOLUTION_RESULT_PRIORITY = {
  passed: 0,
  tied: 1,
  failed: 2,
  '': 3,
};

const today = () => new Date().toISOString().slice(0, 10);
const inDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

// --- Directory ---
export function sortMembers(list) {
  return [...(list || [])].sort((a, b) => {
    const ra = ROLE_PRIORITY[a.role] ?? ROLE_PRIORITY_FALLBACK;
    const rb = ROLE_PRIORITY[b.role] ?? ROLE_PRIORITY_FALLBACK;
    if (ra !== rb) return ra - rb;
    return (a.name || '').localeCompare(b.name || '');
  });
}

// --- Committees ---
export function sortCommittees(list) {
  return [...(list || [])].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
}

// --- Meetings: split upcoming / past ---
export function splitMeetings(list) {
  const t = today();
  const upcoming = [];
  const past = [];
  for (const m of list || []) {
    if (!m.meeting_date) { past.push(m); continue; }
    if (m.status === 'cancelled') { past.push(m); continue; }
    if (m.meeting_date >= t) upcoming.push(m); else past.push(m);
  }
  upcoming.sort((a, b) => {
    const c = (a.meeting_date || '').localeCompare(b.meeting_date || '');
    if (c !== 0) return c;
    return (a.time_start || '').localeCompare(b.time_start || '');
  });
  past.sort((a, b) => {
    const c = (b.meeting_date || '').localeCompare(a.meeting_date || '');
    if (c !== 0) return c;
    return (b.time_start || '').localeCompare(a.time_start || '');
  });
  return { upcoming, past };
}

// --- Resolutions: group by meeting (date desc), within each meeting newest first ---
export function groupResolutionsByMeeting(resolutions, meetings) {
  const meetingMap = new Map((meetings || []).map(m => [m.id, m]));
  const groups = new Map();
  for (const r of resolutions || []) {
    const key = r.meeting_id || 'unlinked';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(r);
  }
  for (const arr of groups.values()) {
    arr.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
  }
  const ordered = [];
  for (const [meetingId, items] of groups.entries()) {
    const meeting = meetingMap.get(meetingId);
    ordered.push({ meetingId, meeting, items });
  }
  ordered.sort((a, b) => {
    if (a.meetingId === 'unlinked') return 1;
    if (b.meetingId === 'unlinked') return -1;
    const da = a.meeting?.meeting_date || '';
    const db = b.meeting?.meeting_date || '';
    return db.localeCompare(da);
  });
  return ordered;
}

// --- Documents: expiring first, then by type, then newest ---
export function sortDocuments(list) {
  const t = today();
  const t30 = inDays(30);
  return [...(list || [])].sort((a, b) => {
    const ae = a.expires_at && a.expires_at < t ? 0
      : a.expires_at && a.expires_at <= t30 ? 1 : 2;
    const be = b.expires_at && b.expires_at < t ? 0
      : b.expires_at && b.expires_at <= t30 ? 1 : 2;
    if (ae !== be) return ae - be;
    // within bucket: expiring sooner first
    if (ae < 2) {
      const cmp = (a.expires_at || '').localeCompare(b.expires_at || '');
      if (cmp !== 0) return cmp;
    }
    const tcmp = (a.doc_type || '').localeCompare(b.doc_type || '');
    if (tcmp !== 0) return tcmp;
    return (b.created_at || '').localeCompare(a.created_at || '');
  });
}

// --- Action items: bucket by urgency, sort by due date within ---
export function groupActionItems(list) {
  const t = today();
  const t7 = inDays(7);
  const buckets = {
    overdue: [],
    due_soon: [],
    open: [],
    done: [],
  };
  for (const a of list || []) {
    const isDone = a.status === 'done' || a.status === 'cancelled';
    if (isDone) {
      buckets.done.push(a);
      continue;
    }
    if (a.due_date && a.due_date < t) {
      buckets.overdue.push(a);
    } else if (a.due_date && a.due_date <= t7) {
      buckets.due_soon.push(a);
    } else {
      buckets.open.push(a);
    }
  }
  // Within overdue: oldest due first (most overdue)
  buckets.overdue.sort((a, b) => (a.due_date || '').localeCompare(b.due_date || ''));
  // Within due_soon: sooner first
  buckets.due_soon.sort((a, b) => (a.due_date || '').localeCompare(b.due_date || ''));
  // Within open: with due_date first (sorted asc), then no-due-date by created_at desc
  buckets.open.sort((a, b) => {
    if (!!a.due_date !== !!b.due_date) return a.due_date ? -1 : 1;
    if (a.due_date) return a.due_date.localeCompare(b.due_date);
    return (b.created_at || '').localeCompare(a.created_at || '');
  });
  // Done: most-recently-updated first
  buckets.done.sort((a, b) => (b.updated_at || b.created_at || '').localeCompare(a.updated_at || a.created_at || ''));
  return buckets;
}

// --- Disclosures: missing first, then by member name ---
export function sortDisclosureRows(members, findDisclosure) {
  return [...(members || [])].sort((a, b) => {
    const da = findDisclosure(a.id);
    const db = findDisclosure(b.id);
    const ma = da ? 1 : 0;   // 0 = missing (goes first)
    const mb = db ? 1 : 0;
    if (ma !== mb) return ma - mb;
    return (a.name || '').localeCompare(b.name || '');
  });
}

// --- KPIs: active first, then alphabetical ---
export function sortKPIs(list) {
  return [...(list || [])].sort((a, b) => {
    const aa = a.is_active === false ? 1 : 0;
    const bb = b.is_active === false ? 1 : 0;
    if (aa !== bb) return aa - bb;
    return (a.name || '').localeCompare(b.name || '');
  });
}

// --- Access tab: admins first, then by name ---
export function sortAccessUsers(list) {
  return [...(list || [])].sort((a, b) => {
    const ra = a.role === 'administrator' ? 0 : 1;
    const rb = b.role === 'administrator' ? 0 : 1;
    if (ra !== rb) return ra - rb;
    return (a.name || '').localeCompare(b.name || '');
  });
}
